// 账号可见范围 —— 前端这一层只负责"不显示"和"进不去"。
//
// 真正的隔离在后端 (common/nav_scope.py + WithinAccountScope): 菜单藏了但接口还通,
// 等于没藏。这里做的是让受限账号看不到、点不到, 以及直接敲 URL 时给一个明确的
// "未开通", 而不是一个半截渲染的空页面。
//
// 白名单里的 key 就是导航 key 本身: 模块 key ("/fracsim") 或页面 key
// ("/fracsim/sensitivity")。空 / null = 不限制。

export type NavChildLike = { key: string; aliases?: readonly string[] };
export type NavItemLike<C extends NavChildLike> = { key: string; children?: C[] };

/** 路径是否落在某个 key 之下 (/formation 命中 /formation/layers)。 */
const under = (base: string, path: string) => path === base || path.startsWith(base + '/');

/** 白名单里有没有覆盖到这个 key —— 命中自身, 或命中它的上层。 */
const covered = (whitelist: string[], key: string) =>
  whitelist.some((allowed) => allowed === key || under(allowed, key));

/**
 * 按白名单裁剪导航树。
 * - 模块 key 在白名单里 → 整个模块连同全部子页保留
 * - 只有部分页面 key 在白名单里 → 模块保留, 但只留那几页
 * - 一个都没命中 → 整个模块去掉
 */
export function filterNav<C extends NavChildLike, T extends NavItemLike<C>>(
  items: T[],
  whitelist?: string[] | null,
): T[] {
  if (!whitelist || whitelist.length === 0) return items;
  const out: T[] = [];
  for (const item of items) {
    const wholeModule = whitelist.includes(item.key);
    if (!item.children) {
      if (wholeModule) out.push(item);
      continue;
    }
    if (wholeModule) { out.push(item); continue; }
    const children = item.children.filter((c) =>
      covered(whitelist, c.key) || (c.aliases ?? []).some((alias) => covered(whitelist, alias)));
    if (children.length) out.push({ ...item, children } as T);
  }
  return out;
}

// 与模块无关、任何账号都要能走的路径 (选项目、登录态跳转)
const ALWAYS_ALLOWED = ['/projects', '/login', '/'];

/**
 * 当前路径对这个账号是否开放。裁剪后的导航就是判据 —— 菜单里没有的地方进不去,
 * 免得白名单和路由表两份清单各说各话。
 */
export function isPathAllowed<C extends NavChildLike, T extends NavItemLike<C>>(
  visibleItems: T[],
  pathname: string,
  whitelist?: string[] | null,
): boolean {
  if (!whitelist || whitelist.length === 0) return true;
  if (ALWAYS_ALLOWED.includes(pathname)) return true;
  for (const item of visibleItems) {
    if (!item.children) {
      if (under(item.key, pathname)) return true;
      continue;
    }
    if (item.children.some((c) => under(c.key, pathname) || (c.aliases ?? []).some((alias) => under(alias, pathname)))) return true;
    // 模块整体开放时, 模块前缀下的任意路径都放行 (含还没进菜单的详情页)
    if (whitelist.includes(item.key) && under(item.key, pathname)) return true;
  }
  return false;
}
